/**
 * Export / Import / Version-restore for full semantic (data) models.
 * - ImportModelButton: upload an .inzara-model.json → a dialog previews the
 *   bundle, lets the admin optionally remap it onto a different data source and
 *   map each physical table to a target-warehouse table, and (on a name clash)
 *   choose Overwrite (keeps a revertible version) or Create a copy.
 * - ModelCardActions: per-model Export + Version history (diff + restore).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Upload, History, Loader2, RotateCcw, Database, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

export async function downloadModelExport(modelId, name) {
  const bundle = await api.exportSemanticModel(modelId);
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(name || 'model').replace(/\s+/g, '_')}.inzara-model.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const KEEP = '__keep__';

function ImportDiff({ diff }) {
  const empty = !diff.entities_added.length && !diff.entities_removed.length && !diff.entities_changed.length
    && !diff.measures_added.length && !diff.measures_removed.length && !diff.measures_changed.length;
  const Row = ({ label, items, cls }) => (items?.length ? (
    <p className="text-[10px]"><span className={cls}>{label}:</span> <span className="text-muted-foreground">{items.join(', ')}</span></p>
  ) : null);
  return (
    <div className="rounded-lg border border-border/40 bg-muted/20 p-2.5 space-y-0.5" data-testid="import-diff">
      <p className="text-[10px] text-muted-foreground/80 mb-0.5">Overwriting will change:</p>
      {empty && <p className="text-[10px] text-muted-foreground">No structural differences from the current model.</p>}
      <Row label="Entities added" items={diff.entities_added} cls="text-emerald-400" />
      <Row label="Entities removed" items={diff.entities_removed} cls="text-rose-400" />
      <Row label="Entities changed" items={diff.entities_changed.map(e => e.name)} cls="text-amber-400" />
      <Row label="Measures added" items={diff.measures_added} cls="text-emerald-400" />
      <Row label="Measures removed" items={diff.measures_removed} cls="text-rose-400" />
      <Row label="Measures changed" items={diff.measures_changed} cls="text-amber-400" />
    </div>
  );
}

export function ImportModelButton({ onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState(null); // { bundle, probe }
  const [mode, setMode] = useState(null); // 'overwrite' | 'new'
  const [remap, setRemap] = useState(false);
  const [sources, setSources] = useState([]);
  const [targetSource, setTargetSource] = useState('');
  const [targetTables, setTargetTables] = useState(null); // null=not loaded
  const [targetCols, setTargetCols] = useState({}); // { tableName: [colNames] }
  const [tableMap, setTableMap] = useState({});
  const [colMap, setColMap] = useState({}); // { origTable: { oldCol: newCol } }
  const [expandCols, setExpandCols] = useState({}); // { origTable: bool }
  const [loadingTables, setLoadingTables] = useState(false);

  const reset = () => { setDlg(null); setMode(null); setRemap(false); setTargetSource(''); setTargetTables(null); setTargetCols({}); setTableMap({}); setColMap({}); setExpandCols({}); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    let bundle;
    try { bundle = JSON.parse(await file.text()); }
    catch { toast.error('That file is not valid JSON'); return; }
    if (!bundle?._inzara_model_export) { toast.error('Not an INZARA model export file'); return; }
    setBusy(true);
    try {
      const probe = await api.importSemanticModel(bundle, { probe: true });
      setDlg({ bundle, probe });
      setMode(probe.conflict ? 'overwrite' : 'new');
    } catch (err) {
      toast.error(err.message || 'Could not read that model');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (remap && sources.length === 0) {
      api.getDataSources().then(s => setSources(s || [])).catch(() => setSources([]));
    }
  }, [remap, sources.length]);

  const pickSource = async (sid) => {
    setTargetSource(sid);
    setTargetTables(null);
    setTargetCols({});
    setLoadingTables(true);
    try {
      const res = await api.introspectDataSource(sid);
      const list = res?.tables || [];
      // The introspect API returns tables keyed as `table_name` (with a
      // `display_name`) and columns as `{name}`. Read those keys first, and
      // hard-guard to non-empty STRINGS — a non-string/empty value handed to a
      // Radix <SelectItem value> throws synchronously and white-screens the page.
      const tName = (t) => (typeof t === 'string' ? t : (t?.table_name || t?.name || t?.table || t?.display_name));
      const asStr = (v) => (typeof v === 'string' && v.trim() ? v : null);
      const names = list.map(t => asStr(tName(t))).filter(Boolean);
      const cmap = {};
      list.forEach(t => {
        const tn = asStr(tName(t));
        if (tn) cmap[tn] = (t.columns || []).map(c => asStr(typeof c === 'string' ? c : (c?.name || c?.column))).filter(Boolean);
      });
      setTargetCols(cmap);
      setTargetTables(names.length ? names : (asStr(res?.table) ? [res.table] : []));
    } catch {
      setTargetTables([]);
    } finally { setLoadingTables(false); }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      let mapping = null;
      if (remap && targetSource) {
        const src = sources.find(s => s.source_id === targetSource);
        const tables = {};
        Object.entries(tableMap).forEach(([oldT, newT]) => { if (newT && newT !== KEEP) tables[oldT] = newT; });
        const columns = {};
        Object.entries(colMap).forEach(([oldT, cm]) => {
          const kept = {};
          Object.entries(cm || {}).forEach(([oc, nc]) => { if (nc && nc !== KEEP && nc !== oc) kept[oc] = nc; });
          if (Object.keys(kept).length) columns[oldT] = kept;
        });
        mapping = { source_id: targetSource, source_name: src?.name || '', tables, columns };
      }
      const res = await api.importSemanticModel(dlg.bundle, { mode: dlg.probe.conflict ? mode : 'new', mapping });
      toast.success(res.mode === 'overwrite'
        ? `Updated "${res.name}" — previous version saved`
        : `Imported "${res.name}" (${res.entities} entities, ${res.measures} measures)`);
      reset();
      onImported?.();
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".json,application/json" className="hidden" onChange={onFile} data-testid="import-model-input" />
      <Button variant="outline" className="rounded-xl" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="import-model-btn">
        {busy && !dlg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import
      </Button>

      <Dialog open={!!dlg} onOpenChange={(o) => { if (!o) reset(); }}>
        <DialogContent className="max-w-lg" data-testid="import-model-dialog">
          <DialogHeader><DialogTitle className="text-sm">Import "{dlg?.probe?.name}"</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs">
            {dlg?.probe?.entities} entities · {dlg?.probe?.relationships} relationships · {dlg?.probe?.measures} measures
            {dlg?.probe?.source_name ? ` · from "${dlg.probe.source_name}"` : ''}
          </DialogDescription>

          {dlg?.probe?.conflict && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2.5 space-y-2" data-testid="import-conflict-dialog">
              <p className="text-xs text-amber-300">A model named "{dlg.probe.name}" already exists.</p>
              <div className="flex gap-2">
                <button onClick={() => setMode('overwrite')} className={`flex-1 rounded-lg border px-2.5 py-1.5 text-left ${mode === 'overwrite' ? 'border-primary bg-primary/10' : 'border-border/50'}`} data-testid="import-overwrite">
                  <p className="text-xs font-medium text-foreground">Overwrite &amp; keep version</p>
                  <p className="text-[10px] text-muted-foreground">Snapshots the current model so you can revert.</p>
                </button>
                <button onClick={() => setMode('new')} className={`flex-1 rounded-lg border px-2.5 py-1.5 text-left ${mode === 'new' ? 'border-primary bg-primary/10' : 'border-border/50'}`} data-testid="import-create-copy">
                  <p className="text-xs font-medium text-foreground">Create a copy</p>
                  <p className="text-[10px] text-muted-foreground">Imports as "{dlg.probe.name} (imported)".</p>
                </button>
              </div>
            </div>
          )}

          {dlg?.probe?.conflict && mode === 'overwrite' && dlg?.probe?.diff && (
            <ImportDiff diff={dlg.probe.diff} />
          )}

          <div className="rounded-lg border border-border/50 p-2.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <label className="text-xs text-foreground flex items-center gap-1.5"><Database className="w-3.5 h-3.5 text-muted-foreground" /> Remap data source &amp; tables</label>
              <Switch checked={remap} onCheckedChange={setRemap} data-testid="import-remap-toggle" />
            </div>
            {remap && (
              <div className="space-y-2">
                {!targetSource && <p className="text-[10px] text-muted-foreground">Pick a target data source to map tables and columns.</p>}
                <Select value={targetSource} onValueChange={pickSource}>
                  <SelectTrigger className="h-8 text-xs" data-testid="import-target-source"><SelectValue placeholder="Choose a target data source" /></SelectTrigger>
                  <SelectContent>
                    {sources.map(s => <SelectItem key={s.source_id} value={s.source_id} className="text-xs">{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {loadingTables && <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Reading tables…</div>}
                {targetTables && targetTables.length > 0 && (
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Map each table (and its columns) to the target warehouse</p>
                    {(dlg?.probe?.tables || []).map(oldT => {
                      const mappedTo = tableMap[oldT] && tableMap[oldT] !== KEEP ? tableMap[oldT] : oldT;
                      const tCols = targetCols[mappedTo] || [];
                      const srcCols = (dlg?.probe?.columns || {})[oldT] || [];
                      return (
                        <div key={oldT} className="rounded-md border border-border/40 bg-muted/10 p-1.5" data-testid={`import-map-row-${oldT}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-muted-foreground font-mono truncate flex-1" title={oldT}>{oldT}</span>
                            <Select value={tableMap[oldT] || KEEP} onValueChange={(v) => setTableMap(m => ({ ...m, [oldT]: v }))}>
                              <SelectTrigger className="h-7 text-[11px] w-40" data-testid={`import-map-select-${oldT}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value={KEEP} className="text-[11px]">— keep "{oldT}" —</SelectItem>
                                {targetTables.filter(t => typeof t === 'string' && t).map(t => <SelectItem key={t} value={t} className="text-[11px]">{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          {srcCols.length > 0 && (
                            <button type="button" onClick={() => setExpandCols(x => ({ ...x, [oldT]: !x[oldT] }))}
                              className="text-[10px] text-primary hover:underline mt-1" data-testid={`import-map-cols-toggle-${oldT}`}>
                              {expandCols[oldT] ? 'Hide columns' : `Map columns (${srcCols.length})`}
                            </button>
                          )}
                          {expandCols[oldT] && (
                            <div className="mt-1 space-y-1">
                              {srcCols.map(oc => (
                                <div key={oc} className="flex items-center gap-2" data-testid={`import-col-row-${oldT}-${oc}`}>
                                  <span className="text-[10px] text-muted-foreground font-mono truncate flex-1" title={oc}>{oc}</span>
                                  <Select value={(colMap[oldT] || {})[oc] || KEEP} onValueChange={(v) => setColMap(m => ({ ...m, [oldT]: { ...(m[oldT] || {}), [oc]: v } }))}>
                                    <SelectTrigger className="h-6 text-[10px] w-36" data-testid={`import-col-select-${oldT}-${oc}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={KEEP} className="text-[10px]">— keep "{oc}" —</SelectItem>
                                      {tCols.filter(c => typeof c === 'string' && c).map(c => <SelectItem key={c} value={c} className="text-[10px]">{c}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                              ))}
                              {tCols.length === 0 && <p className="text-[10px] text-amber-400">Pick a target table above to list its columns.</p>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {targetTables && targetTables.length === 0 && !loadingTables && (
                  <p className="text-[11px] text-amber-400">Couldn't read tables from that source — you can still import and fix bindings later.</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={reset} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={doImport} disabled={busy} data-testid="import-confirm">
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ModelCardActions({ model, onChanged }) {
  const [exporting, setExporting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const doExport = async (e) => {
    e.stopPropagation();
    setExporting(true);
    try { await downloadModelExport(model.model_id, model.name); }
    catch (err) { toast.error(err.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  return (
    <>
      <button onClick={doExport} disabled={exporting}
        className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
        title="Export model as JSON" data-testid={`export-model-${model.model_id}`}>
        {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      </button>
      <button onClick={(e) => { e.stopPropagation(); setHistoryOpen(true); }}
        className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
        title="Version history" data-testid={`model-history-${model.model_id}`}>
        <History className="w-3.5 h-3.5" />
      </button>
      {historyOpen && (
        <ModelVersionsDialog model={model} open={historyOpen} onClose={() => setHistoryOpen(false)} onRestored={onChanged} />
      )}
    </>
  );
}

function VersionDiff({ modelId, version }) {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setOpen(o => !o);
    if (diff || loading) return;
    setLoading(true);
    try { const r = await api.diffModelVersion(modelId, version.version_id); setDiff(r.diff); }
    catch { setDiff(null); }
    finally { setLoading(false); }
  };

  const Row = ({ label, items, cls }) => (items?.length ? (
    <p className="text-[10px]"><span className={cls}>{label}:</span> <span className="text-muted-foreground">{items.join(', ')}</span></p>
  ) : null);

  return (
    <div>
      <button onClick={load} className="text-[10px] text-primary hover:underline inline-flex items-center gap-1" data-testid={`diff-version-${version.version_id}`}>
        <GitCompare className="w-3 h-3" /> {open ? 'Hide changes' : 'Preview changes'}
      </button>
      {open && (
        <div className="mt-1 rounded-md bg-muted/30 border border-border/40 p-2 space-y-0.5">
          {loading && <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Comparing…</div>}
          {diff && (() => {
            const empty = !diff.entities_added.length && !diff.entities_removed.length && !diff.entities_changed.length && !diff.measures_added.length && !diff.measures_removed.length && !diff.measures_changed.length;
            if (empty) return <p className="text-[10px] text-muted-foreground">No differences — restoring changes nothing.</p>;
            return (
              <>
                <p className="text-[10px] text-muted-foreground/80 mb-0.5">If you restore this version:</p>
                <Row label="Entities added" items={diff.entities_added} cls="text-emerald-400" />
                <Row label="Entities removed" items={diff.entities_removed} cls="text-rose-400" />
                <Row label="Entities changed" items={diff.entities_changed.map(e => e.name)} cls="text-amber-400" />
                <Row label="Measures added" items={diff.measures_added} cls="text-emerald-400" />
                <Row label="Measures removed" items={diff.measures_removed} cls="text-rose-400" />
                <Row label="Measures changed" items={diff.measures_changed} cls="text-amber-400" />
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function ModelVersionsDialog({ model, open, onClose, onRestored }) {
  const [versions, setVersions] = useState(null);
  const [restoring, setRestoring] = useState('');

  useEffect(() => {
    let alive = true;
    api.listModelVersions(model.model_id)
      .then((r) => { if (alive) setVersions(r.versions || []); })
      .catch(() => { if (alive) setVersions([]); });
    return () => { alive = false; };
  }, [model.model_id]);

  const restore = async (vid) => {
    setRestoring(vid);
    try {
      await api.restoreModelVersion(model.model_id, vid);
      toast.success('Model restored from version');
      onRestored?.();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Restore failed');
    } finally { setRestoring(''); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()} data-testid="model-versions-dialog">
        <DialogHeader><DialogTitle className="text-sm">Version history — {model.name}</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs">Snapshots are saved automatically before an overwrite-import or a restore.</DialogDescription>
        <div className="max-h-80 overflow-y-auto space-y-1.5 mt-1">
          {versions === null && <div className="flex items-center gap-2 text-xs text-muted-foreground py-4"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
          {versions?.length === 0 && <p className="text-xs text-muted-foreground py-4">No saved versions yet. One is created the next time you overwrite this model on import.</p>}
          {versions?.map((v) => (
            <div key={v.version_id} className="rounded-lg border border-border/40 bg-muted/20 px-3 py-2 space-y-1.5" data-testid={`model-version-${v.version_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-foreground truncate">{v.label || 'Snapshot'}</p>
                  <p className="text-[10px] text-muted-foreground">{v.created_at ? new Date(v.created_at).toLocaleString() : ''} · {v.counts?.entities ?? 0} entities · {v.counts?.measures ?? 0} measures</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0" disabled={!!restoring} onClick={() => restore(v.version_id)} data-testid={`restore-version-${v.version_id}`}>
                  {restoring === v.version_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />} Restore
                </Button>
              </div>
              <VersionDiff modelId={model.model_id} version={v} />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
